import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { getScreener } from '../api';

const S = {
  card: {
    background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 10, padding: '16px 20px',
  },
  toolbar: { display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' },
  input: {
    background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6,
    color: '#101828', padding: '7px 12px', fontSize: 13, width: 180, outline: 'none',
  },
  label: { color: '#6B7280', fontSize: 12 },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  thBase: {
    background: '#F9FAFB', color: '#6B7280', textAlign: 'left',
    padding: '8px 12px', borderBottom: '1px solid #E5E7EB',
    fontWeight: 500, cursor: 'pointer', userSelect: 'none',
    whiteSpace: 'nowrap',
  },
  td: { padding: '7px 12px', borderBottom: '1px solid #F3F4F6' },
  badge: (sig) => ({
    display: 'inline-block', borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 700,
    background: sig === 'BUY'  ? 'rgba(22,163,74,0.12)'
              : sig === 'SELL' ? 'rgba(220,38,38,0.12)'
              : sig === 'HOLD' ? 'rgba(234,179,8,0.12)'
              : 'rgba(107,114,128,0.12)',
    color: sig === 'BUY'  ? '#16A34A'
         : sig === 'SELL' ? '#DC2626'
         : sig === 'HOLD' ? '#B45309'
         : '#6B7280',
  }),
  refreshBtn: {
    background: '#F3F4F6', border: '1px solid #E5E7EB', borderRadius: 6,
    color: '#374151', padding: '6px 12px', fontSize: 12, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 4,
  },
  strategyTag: {
    background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 6,
    color: '#1D4ED8', padding: '4px 10px', fontSize: 12, fontWeight: 500,
  },
};

function SortIcon({ col, sortKey, sortDir }) {
  if (sortKey !== col) return <span style={{ color: '#9CA3AF' }}> ⇅</span>;
  return <span style={{ color: '#2563EB' }}> {sortDir === 'asc' ? '↑' : '↓'}</span>;
}

function formatCacheAge(sec) {
  if (sec < 60)  return `${sec}с тому`;
  if (sec < 3600) return `${Math.floor(sec / 60)} хв тому`;
  return `${Math.floor(sec / 3600)} год тому`;
}

export default function Screener() {
  const [data,          setData]          = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [refreshing,    setRefreshing]    = useState(false);
  const [search,        setSearch]        = useState('');
  const [sortKey,       setSortKey]       = useState('signal');
  const [sortDir,       setSortDir]       = useState('asc');
  const [cacheAge,      setCacheAge]      = useState(0);
  const [strategyName,  setStrategyName]  = useState('—');
  const [strategyLabel, setStrategyLabel] = useState('—');
  const [filterSignal,  setFilterSignal]  = useState('ALL');   // ALL | BUY | SELL | HOLD

  const load = useCallback(async (forceRefresh = false) => {
    try {
      const res = await getScreener(forceRefresh);
      setData(res.data.tickers || []);
      setCacheAge(res.data.cache_age_sec || 0);
      setStrategyName(res.data.strategy_name || '—');
      setStrategyLabel(res.data.strategy_label || res.data.strategy_name || '—');
    } catch (e) {
      console.error('Screener error:', e);
    }
  }, []);

  useEffect(() => {
    load(false).finally(() => setLoading(false));

    // Авто-оновлення кожні 5 хвилин
    const interval = setInterval(() => load(false), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [load]);

  // Лічильник часу кешу
  useEffect(() => {
    const tick = setInterval(() => setCacheAge(a => a + 1), 1000);
    return () => clearInterval(tick);
  }, []);

  async function handleRefresh() {
    setRefreshing(true);
    await load(true);
    setCacheAge(0);
    setRefreshing(false);
  }

  function handleSort(col) {
    if (sortKey === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(col); setSortDir('asc'); }
  }

  const filtered = useMemo(() => {
    let rows = data.filter(r => {
      const matchSearch = r.ticker.toLowerCase().includes(search.toLowerCase());
      const matchSignal = filterSignal === 'ALL' || r.signal === filterSignal ||
                          (filterSignal === 'HOLD' && r.in_portfolio && r.signal === 'HOLD');
      return matchSearch && matchSignal;
    });

    rows = [...rows].sort((a, b) => {
      // Спеціальне сортування для signal: BUY→SELL→HOLD→null
      if (sortKey === 'signal') {
        const order = { BUY: 0, SELL: 1, HOLD: 2, null: 3, undefined: 3 };
        const ao = order[a.signal] ?? 3, bo = order[b.signal] ?? 3;
        return sortDir === 'asc' ? ao - bo : bo - ao;
      }
      const av = a[sortKey], bv = b[sortKey];
      if (av == null) return 1;
      if (bv == null) return -1;
      return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
    return rows;
  }, [data, search, sortKey, sortDir, filterSignal]);

  const buys  = data.filter(r => r.signal === 'BUY').length;
  const sells = data.filter(r => r.signal === 'SELL').length;
  const holds = data.filter(r => r.signal === 'HOLD').length;

  function Th({ col, label }) {
    return (
      <th style={S.thBase} onClick={() => handleSort(col)}>
        {label}<SortIcon col={col} sortKey={sortKey} sortDir={sortDir} />
      </th>
    );
  }

  const noStrategy = strategyName === '—';

  return (
    <div style={S.card}>
      {/* Заголовок */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ color: '#101828', fontWeight: 600, fontSize: 15 }}>Screener</span>
          {noStrategy ? (
            <span style={{ ...S.label, fontSize: 12, color: '#F59E0B' }}>
              ⚠️ Бот не запущено — сигнали недоступні
            </span>
          ) : (
            <span style={S.strategyTag}>
              Сигнали за стратегією: <b>{strategyLabel}</b>
            </span>
          )}
        </div>
        <div style={{ ...S.label, marginTop: 4, fontSize: 11 }}>
          {data.length} тікерів &nbsp;·&nbsp;
          <span style={{ color: '#16A34A' }}>{buys} BUY</span>
          &nbsp;·&nbsp;
          <span style={{ color: '#DC2626' }}>{sells} SELL</span>
          &nbsp;·&nbsp;
          <span style={{ color: '#B45309' }}>{holds} HOLD (в портфелі)</span>
          {cacheAge > 0 && (
            <span style={{ marginLeft: 8, color: '#9CA3AF' }}>
              · оновлено {formatCacheAge(cacheAge)}
            </span>
          )}
        </div>
      </div>

      {/* Панель інструментів */}
      <div style={S.toolbar}>
        <input
          style={S.input} placeholder="🔍 Пошук тікера…"
          value={search} onChange={e => setSearch(e.target.value)}
        />

        {/* Фільтр за сигналом */}
        {['ALL', 'BUY', 'SELL', 'HOLD'].map(f => (
          <button key={f}
            onClick={() => setFilterSignal(f)}
            style={{
              ...S.refreshBtn,
              background: filterSignal === f ? '#2563EB' : '#F3F4F6',
              color:      filterSignal === f ? '#FFF'    : '#374151',
              border:     filterSignal === f ? '1px solid #2563EB' : '1px solid #E5E7EB',
              fontWeight: filterSignal === f ? 600 : 400,
            }}>
            {f === 'ALL' ? 'Всі' : f}
          </button>
        ))}

        <button style={S.refreshBtn} onClick={handleRefresh} disabled={refreshing}>
          {refreshing ? '⏳' : '↻'} Оновити
        </button>
      </div>

      {loading ? (
        <div style={{ color: '#6B7280', padding: 24 }}>Завантаження… (перший раз ~5-10с)</div>
      ) : (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                <Th col="ticker"        label="Тікер"      />
                <Th col="price"         label="Ціна"       />
                <Th col="change_pct_1d" label="Зміна 1д%"  />
                <Th col="signal"        label="Сигнал"     />
                <Th col="in_portfolio"  label="Портфель"   />
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ ...S.td, color: '#6B7280', textAlign: 'center', padding: 24 }}>
                    Немає даних
                  </td>
                </tr>
              )}
              {filtered.map(row => {
                const chg = row.change_pct_1d;
                return (
                  <tr key={row.ticker}
                    style={{ background: row.in_portfolio ? 'rgba(37,99,235,0.04)' : 'transparent' }}>
                    <td style={{ ...S.td, fontWeight: 700, color: '#2563EB' }}>{row.ticker}</td>
                    <td style={S.td}>${row.price?.toFixed(2) ?? '—'}</td>
                    <td style={{ ...S.td, color: chg >= 0 ? '#16A34A' : '#DC2626', fontWeight: 600 }}>
                      {chg >= 0 ? '+' : ''}{chg?.toFixed(2) ?? '—'}%
                    </td>
                    <td style={S.td}>
                      {row.signal ? (
                        <span title={row.signal_reason || ''} style={S.badge(row.signal)}>
                          {row.signal}
                        </span>
                      ) : (
                        <span style={{ color: '#9CA3AF' }}>—</span>
                      )}
                    </td>
                    <td style={S.td}>
                      {row.in_portfolio
                        ? <span style={{ color: '#2563EB' }}>✓ Так</span>
                        : <span style={{ color: '#9CA3AF' }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
